import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import intl from 'components/i18n/ReactIntlWrapper';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { ENTITY_TYPE } from 'common/constants';
import { usePaginationPreference } from 'hooks/usePaginationPreference';
import { useDialog } from 'common/DialogProvider';
import { HRApiWrapper } from 'api/HRApiWrapper';
import ContractDrawer from './ContractDrawer';

interface Props {
  employeeId: number;
  hrApi: HRApiWrapper;
  contractLookups: any;
}

const EmployeeContractsSection: React.FC<Props> = ({ employeeId, hrApi, contractLookups }) => {
  const toast = useToastMessageQueue();
  const { openDialog, closeDialog } = useDialog();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [errors] = useState({});
  const [paginationModel, setPaginationModel] = usePaginationPreference('hrContracts', 10);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrApi.getContractsByEmployee('1', employeeId);
      const data: any[] = res.data?.data ?? [];
      data.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
      setItems(data);
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { fetch(); }, [employeeId]);

  const handleDelete = (id: number) => {
    const dialogId = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('delete.modal.roleRow.message'),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get('delete.modal.delete.button'),
        handleConfirm: async () => {
          try { await hrApi.deleteContract('1', id); fetch(); }
          catch (error) { handleAPIError(error, toast, errors); }
          finally { closeDialog(dialogId); }
        },
      },
      cancelButton: {
        cancelLabel: intl.get('delete.modal.cancel.button'),
        handleCancel: () => closeDialog(dialogId),
      },
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'startDate', headerName: intl.get('hr.contract.startDate'), width: 130,
      valueFormatter: (v: any) => v ? new Date(v).toLocaleDateString() : '',
    },
    {
      field: 'dueDate', headerName: intl.get('hr.contract.dueDate'), width: 130,
      valueFormatter: (v: any) => v ? new Date(v).toLocaleDateString() : '',
    },
    { field: 'contractType', headerName: intl.get('hr.contract.type'), width: 150, valueGetter: (_: any, row: any) => row.type?.typeName ?? '' },
    {
      field: 'contractStatus', headerName: intl.get('hr.contract.status'), width: 130,
      renderCell: (params) => {
        const name = params.row.status?.statusName ?? '';
        const color = name.toLowerCase().includes('active') ? 'success' : name.toLowerCase().includes('terminated') ? 'error' : 'default';
        return <Chip label={name} size="small" color={color as any} />;
      },
    },
    {
      field: 'rate', headerName: intl.get('hr.contract.rate'), width: 120,
      valueFormatter: (v: any) => v != null ? `$${v}` : '',
    },
    { field: 'rateType', headerName: intl.get('hr.contract.rateType'), width: 120, valueGetter: (_: any, row: any) => row.rateType?.typeName ?? '' },
    { field: 'ptoQty', headerName: intl.get('hr.contract.ptoQty'), width: 100 },
    {
      field: 'actions', headerName: '', width: 120, sortable: false,
      renderCell: (params) => (
        <DefaultActions row={params.row} entityType={ENTITY_TYPE.EMPLOYEE}
          onEdit={() => { setSelected(params.row); setDrawerOpen(true); }}
          onDelete={() => handleDelete(params.row.id)}
          editScopes={['recruitment.manage']} deleteScopes={['recruitment.manage']} />
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{intl.get('hr.contract.title')}</Typography>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setSelected(null); setDrawerOpen(true); }}>
          {intl.get('hr.contract.add')}
        </Button>
      </Box>
      <Box style={{ width: '100%', height: 320 }}>
        <DataGrid
          rows={items}
          columns={columns}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25]}
          disableRowSelectionOnClick
        />
      </Box>

      <ContractDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={async (data) => {
          if (selected) {
            await hrApi.updateContract('1', { ...data, id: selected.id });
          } else {
            await hrApi.createContract('1', { ...data, employeeId });
          }
          setDrawerOpen(false);
          fetch();
        }}
        item={selected}
        contractLookups={contractLookups}
      />
    </Box>
  );
};

export default EmployeeContractsSection;
